import React, { useEffect } from "react";
import { Grid, } from "@mui/material";
import { useAppDispatch } from "../../../app/hooks";
import { incrementTimeWrapped, useAllTaus, useSelectedTime, } from "../../../features/surface/surfaceSlice";
import { snapInterpolation } from "../../../app/utils";
import { NEW_WILMA_DATA_AVAILABLE, } from "../../UserVisibleStrings";
import {
    routingTimelineMovementSpeed,
    routingTimelineUpdateInterval,
    wilmaTimelineMovementSpeed,
} from "../../../app/constants";
import { TimelineDateBars } from "./TimelineDateBars";
import TimelineDateBarsSwipeable from "./TimelineDateBarsSwipeable";
import PlayButton from "./PlayButton";
import TimelineInfoBar from "./TimelineInfoBar";
import { getDayStart } from "../geoUtils";
import TimelineSlider from "./TimelineSlider";
import { TimelineContainer, TimelineGridContainer, TimelineInnerContainer, TimelineSliderGrid } from "./TimelineStyle";
import { useHasOngoingFetches, useIsPlaying, usePlaybackSpeed } from "../../../features/time/playbackSlice";
import {
    setCurrentDay,
    useCurrentDay,
    useMaxDataTime,
    useMaxDisplayTime,
    useMinDataTime,
    useMinDisplayTime
} from "../../../features/time/TimelineSlice";
import { useIsLeftBarExpanded, useIsRightBarExpanded } from "../../../features/Drawer/drawerSlice";
import { useIsMobile } from "../../../features/pageWidth/pageWidthSlice";
import { CAN_LOAD_ALL_TAUS } from "../../../features/surface/windowUtils";
import { RiskViewerKind, useRiskViewerStateKind } from "../../../features/riskViewer/riskViewerSlice";
import { useShouldShowAlerts } from "../../../features/alertDisplay/alertDisplaySlice";

type TimelineProps = {
    routeStartTime?: number,
    routeEndTime?: number,
    dataIsStale: boolean,
    refetchRiskData: () => void,
    isRiskError: boolean,
    isLoading: boolean,
};

/**
 * Timeline component.
 * @param {TimelineProps} props - The props for the timeline component.
 * @returns {JSX.Element} The Timeline component.
 */
export default function Timeline( {
    routeStartTime,
    routeEndTime,
    dataIsStale,
    refetchRiskData,
    isRiskError,
    isLoading,
}: Readonly<TimelineProps> )
{
    const dispatch = useAppDispatch();
    const time = useSelectedTime();
    const taus = useAllTaus();
    const playbackSpeed = usePlaybackSpeed();
    const isPlaying = useIsPlaying();
    const isMobile = useIsMobile( );
    const minDataTime = useMinDataTime();
    const maxDataTime = useMaxDataTime();
    const currentDay = useCurrentDay();
    const isRightBarExpanded = useIsRightBarExpanded();
    const isLeftBarExpanded = useIsLeftBarExpanded();
    const riskViewerStateKind = useRiskViewerStateKind();
    const hasOngoingFetches = useHasOngoingFetches();
    const isShowingAlerts = useShouldShowAlerts();

    React.useEffect( () =>
    {
        if ( maxDataTime - minDataTime <= 0 ) return;

        if ( isPlaying )
        {
            const interval = setInterval( () =>
            {
                if ( hasOngoingFetches ) return;
                dispatch(
                    incrementTimeWrapped( {
                        increment:
                            playbackSpeed *
                            ( riskViewerStateKind === RiskViewerKind.TRIP_STATE
                                ? routingTimelineMovementSpeed
                                : ( wilmaTimelineMovementSpeed ) ) *
                            ( CAN_LOAD_ALL_TAUS ? 1 : 0.25 ),
                        min: minDataTime,
                        max: maxDataTime,
                    } )
                );
            }, ( routingTimelineUpdateInterval ) );
            return () => clearInterval( interval ); // useEffect can return a clean up value
        }
    }, [
        isPlaying,
        playbackSpeed,
        minDataTime,
        maxDataTime,
        dispatch,
        routeEndTime,
        routeStartTime,
        hasOngoingFetches,
    ] );

    useEffect( () =>
    {
        if ( currentDay.valueOf() !== getDayStart( time ) )
        {
            dispatch( setCurrentDay( getDayStart( time ) ) );
        }
        if ( time < minDataTime )
        {
            dispatch( setCurrentDay( getDayStart( minDataTime ) ) );
        }
        if ( time > maxDataTime )
        {
            dispatch( setCurrentDay( getDayStart( maxDataTime ) ) );
        }
    }, [ time, currentDay ] );

    const minDisplayTime = useMinDisplayTime();
    const maxDisplayTime = useMaxDisplayTime();
    const displayTime = riskViewerStateKind === RiskViewerKind.TRIP_STATE ? time : snapInterpolation( taus, time );

    return (
        <TimelineContainer>
            <TimelineInnerContainer
                isMobile = {isMobile}
                isRightBarExpanded = {isRightBarExpanded}
                isLeftBarExpanded = {isLeftBarExpanded}
                isVisible = {!isShowingAlerts}
            >
                <Grid container>
                    <TimelineInfoBar
                        isLoading = {isLoading}
                        dataIsStale = {dataIsStale}
                        routeStartTime = {routeStartTime}
                        routeEndTime = {routeEndTime}
                        displayTime = {displayTime}
                        NEW_WILMA_DATA_AVAILABLE = {NEW_WILMA_DATA_AVAILABLE}
                        refetchRiskData = {refetchRiskData}
                    />
                    <TimelineGridContainer container spacing = {1} alignContent = "center" isMobile = {isMobile}>
                        <PlayButton isPlaying = {isPlaying} isMobile = {isMobile}/>
                        <TimelineSliderGrid item xs = {isMobile ? 12 : 10} isMobile = {isMobile}>
                            <TimelineSlider
                                isRiskError = {isRiskError}
                                displayTime = {displayTime}
                            />
                            {isMobile ? (
                                <TimelineDateBarsSwipeable/> )
                                : <TimelineDateBars minDisplayTime = {minDisplayTime} maxDisplayTime = {maxDisplayTime} dayNumberOverride = {undefined}/> }
                        </TimelineSliderGrid>
                    </TimelineGridContainer>
                </Grid>
            </TimelineInnerContainer>
        </TimelineContainer>
    );
}
